/**
 * Federal individual income tax for planning (Form 1040 with its main schedules), in whole
 * dollars, from a year's table. It uses the tax rate schedules (not the $50-band tax tables) and
 * covers the items a planning conversation turns on: ordinary and preferential income, the
 * standard or itemized deduction with the OBBBA rules, QBI, the senior, tips and overtime
 * deductions, AMT, self-employment tax, Additional Medicare tax, NIIT and child credits.
 * It is a projection, not a return: it states what it leaves out (see LIMITATIONS).
 */
import { Exact, roundHalfAwayFromZero } from "../finance/exact";
import type { FilingStatus, YearTable } from "./tables";

export const LIMITATIONS = [
  "Uses the tax rate schedules; the IRS tax tables for income under $100,000 can differ by a few dollars.",
  "Leaves out foreign tax credits, education credits, the earned income credit, the premium tax credit, and unrecaptured §1250 and collectibles gains (taxed at up to 25% and 28%).",
  "Passive activity and at-risk limits are not applied; rental and K-1 amounts are taken as entered.",
  "State itemized deductions remove all state and local taxes (Maryland, Virginia and DC add back only income taxes); DC's phase-out of itemized deductions above $200,000, Maryland's pension exclusion and Virginia's age deduction are not applied, so retirees' state tax may be overstated.",
  "In later years wages and business income grow by the assumption; other figures, including state and local taxes paid, stay as entered.",
  "Maryland's 2025 phase-out of itemized deductions for high incomes is not applied; Maryland's standard deduction after 2026 is indexed by the inflation assumption until the Comptroller publishes it, and is marked estimated.",
  "On a joint plan, Social Security tax on self-employment income and an S corporation salary uses the wage base of the spouse who owns the business, reduced only by that spouse's own wages. When the plan doesn't say who owns the business, the wages are treated as the other spouse's.",
];

export type Spouse = "taxpayer" | "spouse";

export type TaxInputs = {
  filingStatus: FilingStatus;
  /** Ages at the end of the year: the taxpayer, and the spouse when filing jointly. */
  ages: number[];
  blindCount?: number;
  childrenUnder17: number;
  otherDependents: number;
  /** W-2 box 1 (after pre-tax 401(k) deferrals); on a joint plan, both spouses together. */
  wages: number;
  /** Pre-tax 401(k)/403(b) deferrals (Social Security and Medicare wages include them); on a joint plan, both spouses together. */
  deferrals: number;
  /** Joint plans: the spouse's part of `wages` and `deferrals` (the rest is the taxpayer's, the first age). */
  spouseWages?: number;
  spouseDeferrals?: number;
  /** Joint plans: who owns the Schedule C business (and draws its S corporation salary); null when not said. */
  businessOwner?: Spouse | null;
  /** Joint plans: who earns the partnership's self-employment income; null when not said. */
  partnershipOwner?: Spouse | null;
  taxableInterest: number;
  taxExemptInterest: number;
  ordinaryDividends: number;
  qualifiedDividends: number;
  shortTermGains: number;
  longTermGains: number;
  /** Short-term capital loss carryover from last year. */
  capitalLossCarryover: number;
  longTermLossCarryover: number;
  /** Schedule C net profit (or loss). */
  businessIncome: number;
  /** The Schedule C business has elected S corporation status: its profit is K-1 income, not self-employment income. */
  businessIsSCorp: boolean;
  businessIsSstb: boolean;
  businessW2Wages: number;
  businessUbia: number;
  /** S corporation K-1 ordinary income (not self-employment income). */
  sCorpIncome: number;
  /** Partnership K-1 income subject to self-employment tax. */
  partnershipSeIncome: number;
  passThroughIsSstb: boolean;
  passThroughW2Wages: number;
  passThroughUbia: number;
  rentalIncome: number;
  /** IRA distributions and pensions, taxable amount. */
  retirementDistributions: number;
  rothConversion: number;
  socialSecurity: number;
  otherIncome: number;
  // Adjustments to income
  iraDeduction: number;
  hsaDeduction: number;
  seHealthInsurance: number;
  sepContribution: number;
  studentLoanInterest: number;
  otherAdjustments: number;
  // Itemized deductions (amounts paid)
  stateLocalTaxes: number;
  mortgageInterest: number;
  charityCash: number;
  charityNonCash: number;
  medical: number;
  otherItemized: number;
  // OBBBA deductions
  qualifiedTips: number;
  qualifiedOvertime: number;
  // AMT preferences
  isoBargainElement: number;
  privateActivityBondInterest: number;
  // Payments
  withholding: number;
  estimatedPayments: number;
  /** Employer and employee Social Security and Medicare on an S corporation owner's salary. */
  payrollTaxes: number;
};

export const EMPTY_INPUTS: TaxInputs = {
  filingStatus: "single",
  ages: [45],
  blindCount: 0,
  childrenUnder17: 0,
  otherDependents: 0,
  wages: 0,
  deferrals: 0,
  spouseWages: 0,
  spouseDeferrals: 0,
  businessOwner: null,
  partnershipOwner: null,
  taxableInterest: 0,
  taxExemptInterest: 0,
  ordinaryDividends: 0,
  qualifiedDividends: 0,
  shortTermGains: 0,
  longTermGains: 0,
  capitalLossCarryover: 0,
  longTermLossCarryover: 0,
  businessIncome: 0,
  businessIsSCorp: false,
  businessIsSstb: false,
  businessW2Wages: 0,
  businessUbia: 0,
  sCorpIncome: 0,
  partnershipSeIncome: 0,
  passThroughIsSstb: false,
  passThroughW2Wages: 0,
  passThroughUbia: 0,
  rentalIncome: 0,
  retirementDistributions: 0,
  rothConversion: 0,
  socialSecurity: 0,
  otherIncome: 0,
  iraDeduction: 0,
  hsaDeduction: 0,
  seHealthInsurance: 0,
  sepContribution: 0,
  studentLoanInterest: 0,
  otherAdjustments: 0,
  stateLocalTaxes: 0,
  mortgageInterest: 0,
  charityCash: 0,
  charityNonCash: 0,
  medical: 0,
  otherItemized: 0,
  qualifiedTips: 0,
  qualifiedOvertime: 0,
  isoBargainElement: 0,
  privateActivityBondInterest: 0,
  withholding: 0,
  estimatedPayments: 0,
  payrollTaxes: 0,
};

export type TaxResult = {
  year: number;
  tableEstimated: boolean;
  totalIncome: number;
  socialSecurityTaxable: number;
  capitalGainOrLoss: number;
  seTax: number;
  adjustments: number;
  agi: number;
  standardDeduction: number;
  /** Itemized deductions after the 2026 §68 limit (the limit is `itemizedLimit`). */
  itemizedDeductions: number;
  /** The §68 reduction for the 37% bracket (2026 on); zero when the return doesn't itemize. */
  itemizedLimit: number;
  itemizing: boolean;
  saltDeducted: number;
  deduction: number;
  seniorDeduction: number;
  tipsDeduction: number;
  overtimeDeduction: number;
  qbiDeduction: number;
  taxableIncome: number;
  preferentialIncome: number;
  ordinaryTax: number;
  capitalGainsTax: number;
  regularTax: number;
  amt: number;
  credits: number;
  refundableCredits: number;
  additionalMedicare: number;
  niit: number;
  payrollTaxes: number;
  totalTax: number;
  payments: number;
  balanceDue: number;
  marginalRate: number;
  bracketRate: number;
  effectiveRate: number;
  /** Top of the bracket the last dollar of ordinary income falls in (null at the top rate). */
  bracketTop: number | null;
  ordinaryTaxableIncome: number;
};

const RATES = [0.1, 0.12, 0.22, 0.24, 0.32, 0.35, 0.37];
const pos = (x: number) => Math.max(0, x);
const overlap = (a0: number, a1: number, b0: number, b1: number) => pos(Math.min(a1, b1) - Math.max(a0, b0));

// --- Exact money arithmetic ----------------------------------------------------------------------
// Every rate that produces a figure the plan shows (bracket, capital gains, self-employment,
// Additional Medicare, NIIT, AMT and payroll rates) multiplies an exact amount and is rounded once,
// half away from zero (finance/exact.ts). Phase-outs that only move a deduction stay in floating
// point and are rounded where they are shown.
/** An amount as an exact value (amounts are entered in whole dollars, or cents at most). */
export function exactOf(n: number): Exact {
  if (Number.isInteger(n)) return Exact.int(n);
  try {
    return Exact.decimal(String(n));
  } catch {
    return Exact.decimal(n.toFixed(12));
  }
}
const ZERO = Exact.int(0);
const rate = (s: string) => Exact.decimal(s);
const emin = (a: Exact, b: Exact) => (a.cmp(b) <= 0 ? a : b);
const emax = (a: Exact, b: Exact) => (a.cmp(b) >= 0 ? a : b);
const epos = (a: Exact) => emax(a, ZERO);
/** Back to a plain number, for amounts that feed later steps (never shown unrounded). */
export const numOf = (e: Exact) => Number(e.toFixed(9));
/** Whole dollars by the system's rule. */
export const wholeDollars = (e: Exact) => e.toCents();
const r0 = roundHalfAwayFromZero;
const RATES_X = ["0.10", "0.12", "0.22", "0.24", "0.32", "0.35", "0.37"].map(rate);
const SE_FACTOR = rate("0.9235");
const SE_SS = rate("0.124");
const SE_MEDICARE = rate("0.029");
const ADDL_MEDICARE = rate("0.009");
const NIIT = rate("0.038");
/** Social Security and Medicare, each side of an employee's pay (IRC §§3101, 3111). */
export const FICA = { socialSecurity: rate("0.062"), medicare: rate("0.0145") };
/** min(a1, b1) − max(a0, b0), at least zero; `b1` null is unbounded. */
function overlapX(a0: Exact, a1: Exact, b0: Exact, b1: Exact | null): Exact {
  const hi = b1 ? emin(a1, b1) : a1;
  return epos(hi.sub(emax(a0, b0)));
}

function bracketTaxX(t: YearTable, status: FilingStatus, income: Exact): Exact {
  const tops = t.brackets[status];
  let tax = ZERO;
  let low = ZERO;
  for (let i = 0; i < RATES_X.length; i++) {
    const high = i < tops.length ? Exact.int(tops[i]!) : null;
    tax = tax.add(overlapX(ZERO, income, low, high).mul(RATES_X[i]!));
    if (!high) break;
    low = high;
  }
  return tax;
}

/** Tax on ordinary income from the rate schedule. */
export function bracketTax(t: YearTable, status: FilingStatus, income: number): number {
  return numOf(bracketTaxX(t, status, exactOf(income)));
}

function bracketOf(t: YearTable, status: FilingStatus, income: number): { rate: number; top: number | null } {
  const tops = t.brackets[status];
  for (let i = 0; i < tops.length; i++) if (income < tops[i]!) return { rate: RATES[i]!, top: tops[i]! };
  return { rate: RATES[RATES.length - 1]!, top: null };
}

/** Ordinary income taxed by the schedule and preferential income stacked on top at 0/15/20%. */
function taxWithPreferences(t: YearTable, status: FilingStatus, taxable: Exact, preferential: Exact) {
  const pref = emin(epos(preferential), epos(taxable));
  const ordinary = taxable.sub(pref);
  const [zeroTop, fifteenTop] = t.capGains[status];
  const ordinaryTax = bracketTaxX(t, status, ordinary);
  const capitalGainsTax = overlapX(ordinary, taxable, Exact.int(zeroTop), Exact.int(fifteenTop))
    .mul(rate("0.15"))
    .add(overlapX(ordinary, taxable, Exact.int(fifteenTop), null).mul(rate("0.20")));
  // The worksheet never charges more than the schedule would on everything.
  const all = bracketTaxX(t, status, taxable);
  return ordinaryTax.add(capitalGainsTax).cmp(all) <= 0 ? { ordinaryTax, capitalGainsTax } : { ordinaryTax: all, capitalGainsTax: ZERO };
}

/** Taxable Social Security benefits (the provisional income worksheet). */
export function taxableSocialSecurity(status: FilingStatus, benefits: number, otherIncome: number): number {
  if (benefits <= 0) return 0;
  const [b1, b2] = status === "mfj" ? [32_000, 44_000] : status === "mfs" ? [0, 0] : [25_000, 34_000];
  const provisional = otherIncome + benefits / 2;
  if (provisional <= b1) return 0;
  if (provisional <= b2) return Math.min(0.5 * (provisional - b1), 0.5 * benefits);
  return Math.min(0.85 * benefits, 0.85 * (provisional - b2) + Math.min(0.5 * (b2 - b1), 0.5 * benefits));
}

/**
 * A reduction of `amount` for each `step` of `excess` (at least zero). "floor" counts only full steps
 * and drops any fraction (Schedule 1-A lines 11 and 19, tips and overtime); "ceil" counts a fraction
 * as a full step (the child tax credit, §24(b)(1): "$50 for each $1,000 (or fraction thereof)"). The
 * excess is taken to the cent first, so float noise can't add or drop a step.
 */
export function stepReduction(excess: number, step: number, amount: number, mode: "floor" | "ceil"): number {
  const cents = r0(pos(excess) * 100);
  const stepCents = r0(step * 100);
  const steps = mode === "floor" ? Math.floor(cents / stepCents) : Math.ceil(cents / stepCents);
  return steps * amount;
}

function inPhase(phase: { from: number; to: number } | undefined, year: number) {
  return !!phase && year >= phase.from && year <= phase.to;
}

/**
 * A joint plan with wages and self-employment income (or a business that may pay its owner a
 * salary) that doesn't say which spouse owns the business. Until it does, the wages are treated as
 * the other spouse's: the owner's Social Security wage base isn't reduced by them (the higher-tax
 * reading). Staff are asked "Say whose wages these are".
 */
export function businessOwnerMissing(x: TaxInputs): boolean {
  if (x.filingStatus !== "mfj" || x.wages + x.deferrals <= 0) return false;
  return (x.businessIncome !== 0 && !x.businessOwner) || (x.partnershipSeIncome !== 0 && !x.partnershipOwner);
}

/**
 * The Social Security and Medicare wages of a person, by who they are on the plan. On a joint plan
 * each spouse has their own wage base (IRC §1402(b)(1), §3121(a)(1)); `null` (owner not said) is a
 * person with no wages.
 */
export function ficaWagesOf(x: TaxInputs, who: Spouse | null): number {
  if (x.filingStatus !== "mfj") return x.wages + x.deferrals;
  if (!who) return 0;
  const sw = Math.min(Math.max(0, x.spouseWages ?? 0), Math.max(0, x.wages));
  const sd = Math.min(Math.max(0, x.spouseDeferrals ?? 0), Math.max(0, x.deferrals));
  return who === "spouse" ? sw + sd : x.wages - sw + (x.deferrals - sd);
}

/**
 * Self-employment tax (Schedule SE), per person: each spouse's net earnings over $400, with Social
 * Security up to the wage base less that spouse's own wages.
 */
export function selfEmployment(t: YearTable, x: TaxInputs): { seIncome: number; seBase: Exact; seTax: Exact; halfSe: Exact } {
  const sched = x.businessIsSCorp ? 0 : x.businessIncome;
  const pship = x.partnershipSeIncome;
  const joint = x.filingStatus === "mfj";
  // Each earner of self-employment income: [their wages, their self-employment income].
  const earners = new Map<string, { wages: number; se: number }>();
  const add = (who: Spouse | null, se: number) => {
    if (!se) return;
    const key = joint ? (who ?? "unsaid") : "one";
    const e = earners.get(key) ?? { wages: joint ? ficaWagesOf(x, who) : x.wages + x.deferrals, se: 0 };
    e.se += se;
    earners.set(key, e);
  };
  add(x.businessOwner ?? null, sched);
  add(x.partnershipOwner ?? null, pship);
  let seBase = ZERO;
  let seTax = ZERO;
  const wageBase = Exact.int(t.ssWageBase);
  for (const e of earners.values()) {
    const base = exactOf(e.se).mul(SE_FACTOR);
    if (base.cmp(Exact.int(400)) < 0) continue;
    seBase = seBase.add(base);
    seTax = seTax.add(SE_SS.mul(emin(base, epos(wageBase.sub(exactOf(e.wages)))))).add(SE_MEDICARE.mul(base));
  }
  return { seIncome: sched + pship, seBase, seTax, halfSe: seTax.div(Exact.int(2)) };
}

export function computeTax(t: YearTable, x: TaxInputs): TaxResult {
  const st = x.filingStatus;
  const people = st === "mfj" ? x.ages.slice(0, 2) : x.ages.slice(0, 1);
  const over65 = people.filter((a) => a >= 65).length;

  // Self-employment tax (Schedule SE), per person.
  const se = selfEmployment(t, x);
  const seIncome = se.seIncome;
  const halfSe = numOf(se.halfSe);
  const ssWages = x.wages + x.deferrals;

  // Capital gains and losses (Schedule D), with the loss limit.
  let st_ = x.shortTermGains - x.capitalLossCarryover;
  let lt = x.longTermGains - (x.longTermLossCarryover ?? 0);
  if (st_ < 0 && lt > 0) {
    const use = Math.min(-st_, lt);
    lt -= use;
    st_ += use;
  } else if (lt < 0 && st_ > 0) {
    const use = Math.min(-lt, st_);
    st_ -= use;
    lt += use;
  }
  const net = st_ + lt;
  const lossLimit = st === "mfs" ? 1_500 : 3_000;
  const capitalGainOrLoss = net < 0 ? Math.max(net, -lossLimit) : net;
  const netLtGain = net > 0 ? pos(Math.min(lt, net)) : 0;

  const nonSs =
    x.wages + x.taxableInterest + x.ordinaryDividends + capitalGainOrLoss + x.businessIncome + x.sCorpIncome + x.partnershipSeIncome + x.rentalIncome + x.retirementDistributions + x.rothConversion + x.otherIncome;
  const adjustments = halfSe + x.iraDeduction + x.hsaDeduction + x.seHealthInsurance + x.sepContribution + x.studentLoanInterest + x.otherAdjustments;
  // The benefits worksheet leaves student loan interest out of the adjustments.
  const socialSecurityTaxable = taxableSocialSecurity(st, x.socialSecurity, nonSs - (adjustments - x.studentLoanInterest) + x.taxExemptInterest);
  const totalIncome = nonSs + socialSecurityTaxable;
  const agi = totalIncome - adjustments;
  const magi = agi;

  // Standard deduction, with the additional amount for 65+ or blind.
  const extraEach = st === "mfj" || st === "mfs" ? t.additional65.married : t.additional65.unmarried;
  const charityNonItemizer = t.charitableNonItemizer ? Math.min(x.charityCash, t.charitableNonItemizer[st]) : 0;
  const standardDeduction = t.standard[st] + extraEach * (over65 + (x.blindCount ?? 0));

  // Itemized deductions (Schedule A).
  const saltCap = Math.max(t.salt.floor[st], t.salt.cap[st] - t.salt.rate * pos(magi - t.salt.phaseStart[st]));
  const saltDeducted = Math.min(x.stateLocalTaxes, saltCap);
  // Non-cash gifts to 30% of AGI; cash to what remains of 60% (§170(b)(1)(G)).
  const nonCashAllowed = Math.min(x.charityNonCash, 0.3 * pos(agi));
  let charity = nonCashAllowed + Math.min(x.charityCash, pos(0.6 * pos(agi) - nonCashAllowed));
  if (t.charitableFloorPct) charity = pos(charity - t.charitableFloorPct * pos(agi));
  const medical = pos(x.medical - 0.075 * pos(agi));
  let itemizedDeductions = saltDeducted + x.mortgageInterest + charity + medical + x.otherItemized;

  // Below-the-line deductions any filer may take (Schedule 1-A).
  const seniorDeduction =
    t.senior && inPhase(t.senior.years, t.year) && st !== "mfs" ? people.reduce((a, age) => a + (age >= 65 ? pos(t.senior!.amount - t.senior!.rate * pos(magi - t.senior!.phaseStart[st])) : 0), 0) : 0;
  // Tips and overtime: the amount is capped, then reduced by $100 for each full $1,000 of MAGI over the
  // threshold (Schedule 1-A lines 10–13 and 18–21; lines 11 and 19 drop any fraction).
  const tipsDeduction =
    t.tips && inPhase(t.tips.years, t.year) && st !== "mfs" ? pos(Math.min(x.qualifiedTips, t.tips.cap) - stepReduction(magi - t.tips.phaseStart[st], 1_000, t.tips.perThousand, "floor")) : 0;
  const overtimeDeduction =
    t.overtime && inPhase(t.overtime.years, t.year) && st !== "mfs"
      ? pos(Math.min(x.qualifiedOvertime, t.overtime.cap[st]) - stepReduction(magi - t.overtime.phaseStart[st], 1_000, t.overtime.perThousand, "floor"))
      : 0;
  const otherBelow = seniorDeduction + tipsDeduction + overtimeDeduction;

  // Qualified business income deduction (§199A), from taxable income before it. §199A(e)(1) computes
  // taxable income without regard to §68, so it is worked from the deduction before §68's limit.
  const preferentialRaw = x.qualifiedDividends + netLtGain;
  const seShare = seIncome > 0 ? halfSe / seIncome : 0;
  const scheduleCQbi = x.businessIsSCorp ? x.businessIncome : x.businessIncome - x.businessIncome * seShare - x.sepContribution - (x.businessIncome > 0 ? x.seHealthInsurance : 0);
  const qbiFrom = (tiBeforeQbi: number) => {
    const threshold = t.qbi.threshold[st];
    const range = t.qbi.range[st];
    const f = Math.min(1, pos(tiBeforeQbi - threshold) / range);
    // Each business's QBI, wages and property after the SSTB applicable percentage.
    const businesses = [
      { qbi: scheduleCQbi, sstb: x.businessIsSstb, w2: x.businessW2Wages, ubia: x.businessUbia },
      { qbi: x.sCorpIncome + x.partnershipSeIncome - x.partnershipSeIncome * seShare, sstb: x.passThroughIsSstb, w2: x.passThroughW2Wages, ubia: x.passThroughUbia },
    ]
      .filter((b) => b.qbi)
      .map((b) => {
        const share = b.sstb ? 1 - f : 1;
        return { qbi: b.qbi * share, w2: b.w2 * share, ubia: b.ubia * share };
      });
    const qbiTotal = businesses.reduce((a, b) => a + b.qbi, 0);
    // Losses reduce the profitable businesses pro rata before the wage limits (Reg. §1.199A-1(d)(2)(iii)).
    const gains = businesses.filter((b) => b.qbi > 0);
    const losses = -businesses.filter((b) => b.qbi < 0).reduce((a, b) => a + b.qbi, 0);
    const gainTotal = gains.reduce((a, b) => a + b.qbi, 0);
    let qbiCombined = 0;
    if (gainTotal > losses) {
      for (const b of gains) {
        const net = b.qbi - (losses * b.qbi) / gainTotal;
        const tentative = 0.2 * net;
        const limit = Math.max(0.5 * b.w2, 0.25 * b.w2 + 0.025 * b.ubia);
        let comp = tentative;
        if (tiBeforeQbi > threshold && limit < tentative) comp = f >= 1 ? limit : tentative - (tentative - limit) * f;
        qbiCombined += comp;
      }
    }
    let qbi = Math.min(pos(qbiCombined), 0.2 * pos(tiBeforeQbi - preferentialRaw));
    if (t.qbi.minimum && qbiTotal >= t.qbi.minimum.activeQbiAtLeast) qbi = Math.max(qbi, t.qbi.minimum.amount);
    return Math.min(qbi, tiBeforeQbi);
  };

  // From 2026, itemized deductions are limited for the 37% bracket (a 35% benefit), in this order:
  // 1. itemized deductions before §68 (above); 2. taxable income before QBI with them (or the standard
  // deduction if larger); 3. QBI from that; 4. taxable income for the §68 test = 2 − 3; 5. the §68
  // reduction, 2/37 of the lesser of the itemized deductions and the amount by which 4 plus them
  // exceeds the start of the 37% bracket (§68(a)(2)); 6. the itemized deductions less 5, against the
  // standard deduction. QBI never depends on step 5, so the order has no cycle.
  const standardTotal = standardDeduction + charityNonItemizer;
  const itemizedBeforeLimit = itemizedDeductions;
  let tiBeforeQbi = pos(agi - Math.max(itemizedBeforeLimit, standardTotal) - otherBelow);
  let qbiDeduction = qbiFrom(tiBeforeQbi);
  let itemizedLimit = 0;
  if (t.itemizedLimit37 && itemizedBeforeLimit > standardTotal) {
    const top = t.brackets[st][t.brackets[st].length - 1]!;
    const tiForTest = tiBeforeQbi - qbiDeduction;
    itemizedLimit = (2 / 37) * Math.min(itemizedBeforeLimit, pos(tiForTest + itemizedBeforeLimit - top));
    itemizedDeductions = itemizedBeforeLimit - itemizedLimit;
  }
  const itemizing = itemizedDeductions > standardTotal;
  const deduction = itemizing ? itemizedDeductions : standardTotal;
  // Itemized deductions that §68 brings under the standard deduction: the return takes the standard
  // deduction, and its QBI is worked from taxable income with it.
  if (!itemizing && itemizedBeforeLimit > standardTotal) {
    itemizedLimit = 0;
    tiBeforeQbi = pos(agi - standardTotal - otherBelow);
    qbiDeduction = qbiFrom(tiBeforeQbi);
  }

  const taxableIncome = pos(r0(agi - deduction - otherBelow - qbiDeduction));
  const preferentialIncome = Math.min(pos(preferentialRaw), taxableIncome);
  const tiX = Exact.int(taxableIncome);
  const prefX = exactOf(preferentialIncome);
  const { ordinaryTax, capitalGainsTax } = taxWithPreferences(t, st, tiX, prefX);
  const regularTaxX = ordinaryTax.add(capitalGainsTax);

  // Alternative minimum tax (Form 6251). §68's limit doesn't apply for AMT (§56(b)(1)(F)).
  const amti = exactOf(taxableIncome + (itemizing ? saltDeducted - itemizedLimit : deduction) + x.isoBargainElement + x.privateActivityBondInterest);
  const exemption = epos(Exact.int(t.amt.exemption[st]).sub(exactOf(t.amt.phaseRate).mul(epos(amti.sub(Exact.int(t.amt.phaseStart[st]))))));
  const amtBase = epos(amti.sub(exemption));
  const amtPref = emin(prefX, amtBase);
  const amtOrdinary = amtBase.sub(amtPref);
  const r28 = Exact.int(st === "mfs" ? t.amt.rate28Over / 2 : t.amt.rate28Over);
  const r26x = rate("0.26");
  const r28x = rate("0.28");
  const tmtOrd = r26x.mul(emin(amtOrdinary, r28)).add(r28x.mul(epos(amtOrdinary.sub(r28))));
  // The 0% and 15% bands start from the regular tax's ordinary taxable income (Form 6251 Part III).
  const [zeroTop, fifteenTop] = t.capGains[st];
  const regularOrdinary = tiX.sub(prefX);
  const atZero = emin(amtPref, epos(Exact.int(zeroTop).sub(regularOrdinary)));
  const atFifteen = emin(amtPref.sub(atZero), epos(Exact.int(fifteenTop).sub(regularOrdinary).sub(atZero)));
  const tmtCg = atFifteen.mul(rate("0.15")).add(amtPref.sub(atZero).sub(atFifteen).mul(rate("0.20")));
  const tmtAll = r26x.mul(emin(amtBase, r28)).add(r28x.mul(epos(amtBase.sub(r28))));
  const tmt = emin(tmtOrd.add(tmtCg), tmtAll);
  // Shown rounded: regular tax plus AMT is the tentative minimum tax rounded once.
  const regularTax = wholeDollars(regularTaxX);
  const amt = Math.max(0, wholeDollars(tmt) - regularTax);

  // Child tax credit and credit for other dependents (whole dollars by law).
  const kids = x.childrenUnder17;
  const fullCredit = kids * t.ctc.perChild + x.otherDependents * t.ctc.otherDependent;
  // $50 for each $1,000 of MAGI over the threshold, or fraction of $1,000 (§24(b)(1)).
  const reduction = stepReduction(magi - t.ctc.phaseStart[st], 1_000, 50, "ceil");
  const credit = pos(fullCredit - reduction);
  const nonRefundable = Math.min(credit, regularTax + amt);
  const earned = exactOf(x.wages).add(epos(exactOf(seIncome).sub(se.halfSe)));
  const refundableCredits = Math.min(credit - nonRefundable, kids * t.ctc.refundable, wholeDollars(rate("0.15").mul(epos(earned.sub(Exact.int(2_500))))));

  // Additional Medicare tax and net investment income tax.
  const additionalMedicareX = ADDL_MEDICARE.mul(epos(exactOf(ssWages).add(se.seBase).sub(Exact.int(t.addlMedicareThreshold[st]))));
  const nii = pos(x.taxableInterest + x.ordinaryDividends + capitalGainOrLoss + x.rentalIncome);
  const niitX = NIIT.mul(emin(exactOf(nii), epos(exactOf(magi).sub(Exact.int(t.niitThreshold[st])))));

  // Each row is rounded once and the total is the sum of the rows the plan shows (payroll tax on
  // an S corporation owner's salary is its own row).
  const seTax = wholeDollars(se.seTax);
  const additionalMedicare = wholeDollars(additionalMedicareX);
  const niit = wholeDollars(niitX);
  const payrollTaxes = r0(x.payrollTaxes ?? 0);
  const totalTax = regularTax + amt - nonRefundable + seTax + additionalMedicare + niit + payrollTaxes - refundableCredits;
  const payments = x.withholding + x.estimatedPayments;
  const ordinaryTaxableIncome = taxableIncome - preferentialIncome;
  const bracket = bracketOf(t, st, ordinaryTaxableIncome);
  return {
    year: t.year,
    tableEstimated: t.estimated,
    totalIncome: r0(totalIncome),
    socialSecurityTaxable: r0(socialSecurityTaxable),
    capitalGainOrLoss: r0(capitalGainOrLoss),
    seTax,
    adjustments: r0(adjustments),
    agi: r0(agi),
    standardDeduction: r0(standardDeduction),
    itemizedDeductions: r0(itemizedDeductions),
    itemizedLimit: r0(itemizedLimit),
    itemizing,
    saltDeducted: r0(itemizing ? saltDeducted : 0),
    deduction: r0(deduction),
    seniorDeduction: r0(seniorDeduction),
    tipsDeduction: r0(tipsDeduction),
    overtimeDeduction: r0(overtimeDeduction),
    qbiDeduction: r0(qbiDeduction),
    taxableIncome,
    preferentialIncome: r0(preferentialIncome),
    ordinaryTax: wholeDollars(ordinaryTax),
    capitalGainsTax: wholeDollars(capitalGainsTax),
    regularTax,
    amt,
    credits: nonRefundable,
    refundableCredits,
    additionalMedicare,
    niit,
    payrollTaxes,
    totalTax,
    payments,
    balanceDue: totalTax - payments,
    // What the next $1,000 of ordinary income costs, everything included.
    marginalRate: 0,
    bracketRate: bracket.rate,
    bracketTop: bracket.top,
    effectiveRate: totalIncome > 0 ? totalTax / totalIncome : 0,
    ordinaryTaxableIncome: r0(ordinaryTaxableIncome),
  };
}

/** computeTax plus the all-in marginal rate on the next $1,000 of ordinary income. */
export function computeTaxWithMarginal(t: YearTable, x: TaxInputs): TaxResult {
  const base = computeTax(t, x);
  const more = computeTax(t, { ...x, otherIncome: x.otherIncome + 1_000 });
  return { ...base, marginalRate: (more.totalTax - base.totalTax) / 1_000 };
}
